import { supabase } from '../lib/supabase.js';

export const notificationsService = {
  // Get all notifications for an artist
  async getNotifications(artistId, limit = 20) {
    try {
      const { data, error } = await supabase?.from('artist_notifications')?.select(`
          *,
          notification_deliveries(
            id,
            delivery_status,
            delivered_at,
            error_message
          )
        `)?.eq('artist_id', artistId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to fetch notifications' };
    }
  },

  // Create a new notification
  async createNotification(notificationData) {
    try {
      const { data, error } = await supabase?.from('artist_notifications')?.insert([{
          artist_id: notificationData?.artistId,
          title: notificationData?.title,
          message: notificationData?.message,
          notification_type: notificationData?.type || 'general',
          status: notificationData?.status || 'draft',
          scheduled_for: notificationData?.scheduledFor || null,
          target_audience: notificationData?.targetAudience || {},
          metadata: notificationData?.metadata || {}
        }])?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to create notification' };
    }
  },

  // Update notification
  async updateNotification(notificationId, updates) {
    try {
      const { data, error } = await supabase?.from('artist_notifications')?.update({
          title: updates?.title,
          message: updates?.message,
          notification_type: updates?.type,
          status: updates?.status,
          scheduled_for: updates?.scheduledFor,
          target_audience: updates?.targetAudience,
          metadata: updates?.metadata,
          updated_at: new Date()?.toISOString()
        })?.eq('id', notificationId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to update notification' };
    }
  },

  // Delete notification
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase?.from('artist_notifications')?.delete()?.eq('id', notificationId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error?.message || 'Failed to delete notification' };
    }
  },

  // Send notification immediately
  async sendNotification(notificationId) {
    try {
      // Update status to sent
      const { data, error } = await supabase?.from('artist_notifications')?.update({
          status: 'sent',
          sent_at: new Date()?.toISOString()
        })?.eq('id', notificationId)?.select()?.single();

      if (error) throw error;

      // In a real implementation, this would trigger the actual push notification
      // For now, we'll simulate delivery tracking
      await this.simulateNotificationDelivery(notificationId);

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to send notification' };
    }
  },

  // Schedule notification
  async scheduleNotification(notificationId, scheduledFor) {
    try {
      const { data, error } = await supabase?.from('artist_notifications')?.update({
          status: 'scheduled',
          scheduled_for: scheduledFor
        })?.eq('id', notificationId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to schedule notification' };
    }
  },

  // Get notification analytics
  async getNotificationAnalytics(artistId, timeRange = '30_days') {
    try {
      const dateFilter = this.getDateFilter(timeRange);
      
      const { data: notifications, error: notifError } = await supabase?.from('artist_notifications')?.select(`
          id,
          title,
          notification_type,
          status,
          sent_at,
          created_at,
          notification_deliveries(
            delivery_status,
            delivered_at,
            error_message
          )
        `)?.eq('artist_id', artistId)?.gte('created_at', dateFilter)?.order('created_at', { ascending: false });

      if (notifError) throw notifError;

      // Process analytics data
      const analytics = this.processNotificationAnalytics(notifications);

      return { data: analytics, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to fetch notification analytics' };
    }
  },

  // Get delivery statistics for a specific notification
  async getDeliveryStats(notificationId) {
    try {
      const { data, error } = await supabase?.from('notification_deliveries')?.select('*')?.eq('notification_id', notificationId);

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        delivered: data?.filter(d => d?.delivery_status === 'delivered')?.length || 0,
        failed: data?.filter(d => d?.delivery_status === 'failed')?.length || 0,
        pending: data?.filter(d => d?.delivery_status === 'pending')?.length || 0
      };

      stats.deliveryRate = stats?.total > 0 ? ((stats?.delivered / stats?.total) * 100)?.toFixed(1) : 0;

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to fetch delivery stats' };
    }
  },

  // Helper: Simulate notification delivery (for demo purposes)
  async simulateNotificationDelivery(notificationId) {
    try {
      // Simulate multiple device deliveries
      const simulatedDeliveries = [
        { delivery_status: 'delivered', delivered_at: new Date()?.toISOString() },
        { delivery_status: 'delivered', delivered_at: new Date()?.toISOString() },
        { delivery_status: 'failed', error_message: 'Device token expired' },
        { delivery_status: 'delivered', delivered_at: new Date()?.toISOString() }
      ];

      const deliveriesToInsert = simulatedDeliveries?.map(delivery => ({
        notification_id: notificationId,
        device_token: `device_${Math.random()?.toString(36)?.substr(2, 9)}`,
        ...delivery
      }));

      const { error } = await supabase?.from('notification_deliveries')?.insert(deliveriesToInsert);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to simulate delivery:', error);
    }
  },

  // Helper: Get date filter
  getDateFilter(timeRange) {
    const now = new Date();
    switch (timeRange) {
      case '7_days':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)?.toISOString();
      case '30_days':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)?.toISOString();
      case '90_days':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)?.toISOString();
      default:
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)?.toISOString();
    }
  },

  // Helper: Process notification analytics
  processNotificationAnalytics(notifications) {
    const analytics = {
      totalSent: 0,
      totalDelivered: 0,
      totalFailed: 0,
      deliveryRate: 0,
      typeBreakdown: {},
      recentActivity: [],
      chartData: []
    };

    notifications?.forEach(notification => {
      // Count by status
      if (notification?.status === 'sent') {
        analytics.totalSent++;
      }

      // Count by type
      const type = notification?.notification_type || 'general';
      analytics.typeBreakdown[type] = (analytics?.typeBreakdown?.[type] || 0) + 1;

      // Process deliveries
      notification?.notification_deliveries?.forEach(delivery => {
        if (delivery?.delivery_status === 'delivered') {
          analytics.totalDelivered++;
        } else if (delivery?.delivery_status === 'failed') {
          analytics.totalFailed++;
        }
      });

      // Add to recent activity
      analytics?.recentActivity?.push({
        id: notification?.id,
        title: notification?.title,
        type: notification?.notification_type,
        status: notification?.status,
        sentAt: notification?.sent_at,
        createdAt: notification?.created_at,
        deliveryCount: notification?.notification_deliveries?.length || 0
      });
    });

    // Calculate delivery rate
    const totalAttempts = analytics?.totalDelivered + analytics?.totalFailed;
    analytics.deliveryRate = totalAttempts > 0 
      ? ((analytics?.totalDelivered / totalAttempts) * 100)?.toFixed(1)
      : 0;

    return analytics;
  },

  // Real-time subscription for notifications
  subscribeToNotifications(artistId, callback) {
    return supabase?.channel(`notifications-${artistId}`)?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'artist_notifications',
          filter: `artist_id=eq.${artistId}`
        },
        callback
      )?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'notification_deliveries'
        },
        callback
      )?.subscribe();
  }
};

export default notificationsService;